import { FC, ReactNode, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Alert } from "src/domains/shared/components/Alert";
import { Button } from "src/domains/shared/components/Button";
import { DeleteConfirmDialog } from "src/domains/shared/components/DeleteConfirmDialog";
import { EMAIL_REG_EXP } from "src/domains/shared/constants/constants";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { isCountKnown, resolveQueryRenderState } from "src/domains/shared/utils/queryRenderState";
import { UserOfProject } from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.modal";
import {
  BranchFade,
  CAPTION,
  LineGlyph,
  REVEAL_CLOSE_MS,
  revealCloseCss,
  revealIdleCss,
  revealOpenCss,
  SkeletonBone,
  SkeletonReveal,
  StateSlot,
} from "src/domains/shared/components/StateTransition";
import { ValueSelect } from "src/domains/shared/components/ValueSelect";
import { MYPAGE_CARD } from "./myPageStyles";
import { useUserManagement } from "./hooks/useUserManagement";
import { useProjectOwnerTransfer } from "./hooks/useProjectOwnerTransfer";
import { useTransferOwnerForm } from "./hooks/useTransferOwnerForm";
import { AnimatedListPhase, useAnimatedList } from "./hooks/useAnimatedList";
import { MemberCard } from "./components/MemberCard";
import { AddMemberDialog } from "./components/AddMemberDialog";
import { TransferOwnerDialog } from "./components/TransferOwnerDialog";
import { ChangeBar } from "./components/ChangeBar";

const SKELETON_ROWS = [
  ["30%", "58%"],
  ["24%", "70%"],
  ["28%", "50%"],
  ["22%", "64%"],
  ["26%", "44%"],
  ["30%", "60%"],
  ["20%", "52%"],
] as const;

const PHASE_CSS: Record<AnimatedListPhase, typeof revealIdleCss> = {
  idle: revealIdleCss,
  enter: revealOpenCss,
  exit: revealCloseCss,
};

const memberKey = (member: UserOfProject) => member.id;

export const UserManagement: FC = () => {
  const { t } = useTranslation(["projects", "common"]);
  const manage = useUserManagement();
  const transfer = useProjectOwnerTransfer(manage.selectedProject);
  const transferForm = useTransferOwnerForm(transfer);
  const members = manage.usersData ?? [];
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [addEmail, setAddEmail] = useState("");
  const [addFormatError, setAddFormatError] = useState<string | null>(null);

  const projectsRender = resolveQueryRenderState({
    status: manage.ownProjectListQuery.status,
    fetchStatus: manage.ownProjectListQuery.fetchStatus,
    itemCount: manage.projectOptions.length,
    hasData: manage.ownProjectListQuery.hasData,
  });
  const membersRender = resolveQueryRenderState({
    status: manage.memberListQuery.status,
    fetchStatus: manage.memberListQuery.fetchStatus,
    itemCount: members.length,
    hasData: manage.memberListQuery.hasData,
  });
  const projectsReady = projectsRender.state === "ready";
  const memberEntries = useAnimatedList(members, memberKey, REVEAL_CLOSE_MS, manage.selectedProject);

  const roleOf = (member: UserOfProject) =>
    manage.editTargetUsers.find((target) => target.id === member.id)?.role ?? member.role;
  const deleteTarget = members.find((member) => member.id === deleteTargetId);
  const deleteTargetName = deleteTarget ? deleteTarget.name ?? deleteTarget.username ?? deleteTarget.email : "";
  const retryLabel = t("queryState.retry", { ns: "common" });

  const openAddDialog = () => {
    setAddEmail("");
    setAddFormatError(null);
    manage.handleOpenAddDialog();
  };

  const submitAdd = () => {
    const email = addEmail.trim();
    if (!email) {
      setAddFormatError(t("myPageModal.permission.emailRequired", { ns: "projects" }));
      return;
    }
    if (!EMAIL_REG_EXP.test(email)) {
      setAddFormatError(t("myPageModal.permission.emailInvalid", { ns: "projects" }));
      return;
    }
    manage.handleAddMember({ email });
  };

  const changeAddEmail = (value: string) => {
    setAddEmail(value);
    setAddFormatError(null);
    if (manage.emailError) manage.setEmailError("");
  };

  const isListPending = projectsRender.state === "pending" || (projectsReady && membersRender.state === "pending");
  const canAddMember = projectsReady && membersRender.state !== "pending" && membersRender.state !== "error";
  // 소유권 이전은 고른 프로젝트가 확정된 뒤에만 — 멤버 추가와 같은 조건이다(이 화면은 소유 프로젝트만 고른다).
  const canTransferOwner = canAddMember;

  const listBody: { key: string; node: ReactNode } = (() => {
    if (isListPending) {
      return {
        key: "pending",
        node: (
          <SkeletonReveal label={t("myPageModal.permission.memberList", { ns: "projects" })}>
            {SKELETON_ROWS.map(([nameWidth, metaWidth], index) => (
              <Box key={index} sx={skeletonCardSx}>
                <SkeletonBone width={32} height={32} radius={6} />
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                  <SkeletonBone width={nameWidth} height={11} />
                  <SkeletonBone width={metaWidth} height={9} />
                </Box>
                <SkeletonBone width={150} height={28} radius={8} />
                <Box sx={{ width: 28 }} />
              </Box>
            ))}
          </SkeletonReveal>
        ),
      };
    }
    if (projectsRender.state === "error") {
      return {
        key: "projects-error",
        node: (
          <StateSlot
            tone="error"
            icon="alert"
            title={t("queryState.ownProjectListLoadFailed", { ns: "projects" })}
            retryLabel={retryLabel}
            onRetry={() => void manage.ownProjectListQuery.refetch()}
          />
        ),
      };
    }
    if (projectsRender.state === "empty") {
      return {
        key: "projects-empty",
        node: (
          <StateSlot
            tone="neutral"
            icon="users"
            title={t("myPageModal.permission.noOwnProjectTitle", { ns: "projects" })}
            description={t("myPageModal.permission.noOwnProjectDescription", { ns: "projects" })}
          />
        ),
      };
    }
    if (membersRender.state === "error") {
      return {
        key: "members-error",
        node: (
          <StateSlot
            tone="error"
            icon="alert"
            title={t("queryState.memberListLoadFailed", { ns: "projects" })}
            retryLabel={retryLabel}
            onRetry={() => void manage.memberListQuery.refetch()}
          />
        ),
      };
    }
    if (membersRender.state === "empty" && memberEntries.length === 0) {
      return {
        key: "members-empty",
        node: <StateSlot tone="info" icon="users" title={t("myPageModal.permission.noMembers", { ns: "projects" })} />,
      };
    }
    return {
      key: "members-ready",
      node: (
        <Box component="ul" aria-label={t("myPageModal.permission.memberList", { ns: "projects" })} sx={{ margin: 0, padding: 0, listStyle: "none" }}>
          {memberEntries.map((entry) => {
            const role = roleOf(entry.item);
            return (
              <Box component="li" key={entry.key} css={PHASE_CSS[entry.phase]} aria-hidden={entry.phase === "exit" || undefined}>
                <Box sx={{ paddingBottom: "6px" }}>
                  <MemberCard
                    member={entry.item}
                    role={role}
                    modified={role !== entry.item.role}
                    onChangeRole={(next) => manage.handleRoleChange(entry.item.id, next)}
                    onRequestDelete={() => setDeleteTargetId(entry.item.id)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      ),
    };
  })();

  const memberCountText = isCountKnown(membersRender) && projectsReady ? String(members.length) : "";
  const degradedNotice = (() => {
    if (projectsRender.degraded && isCountKnown(projectsRender)) {
      return t("queryState.ownProjectListLoadFailed", { ns: "projects" });
    }
    if (projectsReady && membersRender.degraded && isCountKnown(membersRender)) {
      return t("queryState.memberListLoadFailed", { ns: "projects" });
    }
    return null;
  })();

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, minHeight: 0, padding: "16px", display: "flex" }}>
        <Box sx={{ ...MYPAGE_CARD, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ height: 56, flexShrink: 0, display: "flex", alignItems: "center", gap: "8px", padding: "0 12px", borderBottom: "1px solid rgba(0, 0, 0, 0.06)" }}>
            <ValueSelect
              searchable
              ariaLabel={t("projectPermission.selectProject", { ns: "projects" })}
              options={manage.projectOptions}
              value={manage.selectedProject}
              onChange={manage.setSelectedProject}
              disabled={!projectsReady}
              placeholder={
                projectsRender.state === "pending"
                  ? t("projectPermission.loading", { ns: "projects" })
                  : t("projectPermission.selectProject", { ns: "projects" })
              }
            />
            <Button
              size="small"
              emphasis="outlined"
              intent="primary"
              disabled={!canAddMember}
              onClick={openAddDialog}
              startIcon={<LineGlyph name="plus" size={14} strokeWidth={2.2} />}
              sx={{ flexShrink: 0 }}
            >
              {t("projectPermission.addMember", { ns: "projects" })}
            </Button>
            <Button
              size="small"
              emphasis="outlined"
              intent="inherit"
              disabled={!canTransferOwner}
              onClick={transferForm.openDialog}
              sx={{ flexShrink: 0 }}
            >
              {t("projectPermission.transferOwner", { ns: "projects" })}
            </Button>
          </Box>
          <Box sx={{ height: 34, flexShrink: 0, display: "flex", alignItems: "flex-end", padding: "0 14px 6px" }}>
            <Typography component="h3" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.03em", color: CAPTION, margin: 0 }}>
              {t("myPageModal.permission.members", { ns: "projects" })}{" "}
              <Box component="span" sx={{ display: "inline-block", minWidth: "3ch", fontVariantNumeric: "tabular-nums" }}>
                {memberCountText}
              </Box>
            </Typography>
          </Box>
          {degradedNotice && (
            <Box sx={{ padding: "0 12px 8px" }}>
              <Alert type="warning" message={degradedNotice} actionLabel={retryLabel} onActionClick={() => {
                void manage.ownProjectListQuery.refetch();
                void manage.memberListQuery.refetch();
              }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 12px 6px", display: "flex", flexDirection: "column" }}>
            <BranchFade key={listBody.key}>{listBody.node}</BranchFade>
          </Box>
        </Box>
      </Box>

      <ChangeBar
        changeCount={manage.editTargetUsers.length}
        saving={manage.isPatching}
        onReset={manage.handleResetChanges}
        onSave={manage.handleSaveChanges}
      />

      <AddMemberDialog
        open={manage.showAddMemberDialog}
        email={addEmail}
        errorText={addFormatError ?? (manage.emailError || null)}
        adding={manage.isCreating}
        onEmailChange={changeAddEmail}
        onSubmit={submitAdd}
        onClose={manage.handleCloseAddDialog}
      />

      <TransferOwnerDialog {...transferForm.dialogProps} />

      <DeleteConfirmDialog
        open={deleteTarget !== undefined}
        title={t("myPageModal.permission.deleteTitle", { ns: "projects" })}
        message={t("myPageModal.permission.deleteMessage", { ns: "projects", name: deleteTargetName })}
        subMessage={t("myPageModal.permission.deleteSubMessage", { ns: "projects" })}
        cancelLabel={t("constants.cancel", { ns: "common" })}
        confirmLabel={t("myPage.permission.actions.delete", { ns: "projects" })}
        onCancel={() => setDeleteTargetId(null)}
        onConfirm={() => {
          if (deleteTargetId !== null) manage.handleDeleteMember(deleteTargetId);
          setDeleteTargetId(null);
        }}
      />
    </Box>
  );
};

const skeletonCardSx = {
  height: 56,
  marginBottom: "6px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "0 8px 0 10px",
  borderRadius: "8px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  backgroundColor: COLOR_CONSTANTS.common.white_states.main,
} as const;
